import { Link } from "react-router-dom";
import { getFeaturedBooks, getFictionBooks, getNonFictionBooks } from "@/data/books";
import BookCard from "@/components/BookCard";
import ScrollReveal from "@/components/ScrollReveal";
import {ArrowRight, BookOpen, Feather, CheckCircle, Clock, Library} from 'lucide-react';
import { useHead } from "@unhead/react";

const writingProjects = [
  {
    id: "ashes-and-oaths",
    title: "Ashes & Oaths",
    genre: "Epic Fantasy",
    status: "forthcoming" as const,
    statusLabel: "Coming Soon",
    series: "The Oath-Ledger, Book I",
  },
  {
    id: "the-gathering",
    title: "The Gathering",
    genre: "Fiction",
    status: "in-progress" as const,
    statusLabel: "Drafting",
  },
  {
    id: "gold-and-amber",
    title: "Gold & Amber",
    genre: "Literary Fiction",
    status: "in-progress" as const,
    statusLabel: "Drafting",
  },
  {
    id: "3-cups-on-the-rooftop",
    title: "3 Cups on the Rooftop",
    genre: "Contemporary Fiction",
    status: "in-progress" as const,
    statusLabel: "Drafting",
  },
  {
    id: "lives-of-the-prophets",
    title: "Lives of the Prophets (AS)",
    genre: "Non-Fiction",
    status: "in-progress" as const,
    statusLabel: "Research & Drafting",
  },
];

export default function Home() {
  const featured = getFeaturedBooks();
  const fictionBooks = getFictionBooks();
  const nonFictionBooks = getNonFictionBooks();

  useHead({
    title: "M. A. AMIN Books | Stories · Knowledge · Legacy",
    meta: [
      { name: "description", content: "Official website of M. A. AMIN Books, home to the fiction and non-fiction works of M. A. AMIN. Epic fantasy, literary fiction, and educational non-fiction." },
    ],
  });

  return (
    <div className="min-h-screen bg-[#FAFAF8] dark:bg-zinc-950">
      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAF8] via-[#FAFAF8] to-[#f0ece4] dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#C5A572]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#C5A572]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Brand pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A572]/10 border border-[#C5A572]/20 mb-8 opacity-0 animate-fade-in-up">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C5A572] animate-pulse" />
              <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A572]">
                M. A. AMIN Books
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-zinc-900 dark:text-white leading-[1.05] mb-6 opacity-0 animate-fade-in-up animation-delay-100">
              Stories That Entertain.
              <br />
              <span className="text-[#C5A572]">Knowledge That Endures.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-zinc-500 dark:text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-10 opacity-0 animate-fade-in-up animation-delay-200">
              Epic fantasy, literary fiction, faith-based works, and educational non-fiction by M. A. AMIN.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up animation-delay-300">
              <Link
                to="/fiction"
                className="group flex items-center gap-3 px-8 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-full text-sm font-semibold transition-all duration-300 hover:bg-[#C5A572] dark:hover:bg-[#C5A572] dark:hover:text-white hover:shadow-xl hover:shadow-[#C5A572]/20"
              >
                <Feather size={16} />
                Explore Fiction
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/non-fiction"
                className="group flex items-center gap-3 px-8 py-4 border-2 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-full text-sm font-semibold transition-all duration-300 hover:border-[#C5A572] hover:text-[#C5A572] dark:hover:border-[#C5A572] dark:hover:text-[#C5A572]"
              >
                <BookOpen size={16} />
                Explore Non-Fiction
              </Link>
              <Link
                to="/library"
                className="group flex items-center gap-3 px-8 py-4 border-2 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-full text-sm font-semibold transition-all duration-300 hover:border-[#C5A572] hover:text-[#C5A572] dark:hover:border-[#C5A572] dark:hover:text-[#C5A572]"
              >
                <Library size={16} />
                View Library
              </Link>
            </div>

            {/* Tagline */}
            <p className="mt-12 text-xs tracking-wider text-zinc-400 dark:text-zinc-500 uppercase opacity-0 animate-fade-in-up animation-delay-400">
              Fiction and Non-Fiction by M. A. AMIN
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-0 animate-fade-in-up animation-delay-600">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-[#C5A572] to-transparent" />
        </div>
      </section>

      {/* ── Collections ── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-4">
                Two Collections, One Vision
              </h2>
              <p className="text-zinc-500 dark:text-zinc-400 max-w-xl mx-auto">
                Stories crafted to entertain the imagination and knowledge designed to expand the mind.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal delay={100} className="h-full">
              <Link
                to="/fiction"
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl bg-zinc-900 dark:bg-zinc-900 p-10 lg:p-14 transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A572]/10"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#C5A572]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-[#C5A572]/20 transition-colors duration-700" />
                <div className="relative z-10">
                  <Feather size={32} className="text-[#C5A572] mb-6" />
                  <h3 className="font-serif text-2xl lg:text-3xl font-bold text-white mb-4">
                    Fiction
                  </h3>
                  <p className="text-zinc-400 leading-relaxed mb-6 max-w-sm">
                    Epic fantasy, literary fiction, and contemporary stories exploring identity, faith, culture, human relationships, and the search for meaning.
                  </p>
                  <p className="text-xs text-zinc-500 mb-8">
                    {fictionBooks.length} works &bull; {fictionBooks.filter(b => b.status === "complete").length} completed
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#C5A572] text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                    Explore Fiction
                    <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={200} className="h-full">
              <Link
                to="/non-fiction"
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl bg-[#C5A572]/5 dark:bg-zinc-900/50 border border-[#C5A572]/10 p-10 lg:p-14 transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A572]/10"
              >
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C5A572]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 group-hover:bg-[#C5A572]/10 transition-colors duration-700" />
                <div className="relative z-10">
                  <BookOpen size={32} className="text-[#C5A572] mb-6" />
                  <h3 className="font-serif text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white mb-4">
                    Non-Fiction
                  </h3>
                  <p className="text-zinc-500 dark:text-zinc-400 leading-relaxed mb-6 max-w-sm">
                    Educational, historical, faith-based, and research-driven works designed to inform, inspire, and preserve knowledge.
                  </p>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-8">
                    {nonFictionBooks.length} work{nonFictionBooks.length !== 1 ? "s" : ""} &bull; In development
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#C5A572] text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                    Explore Non-Fiction
                    <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ── Featured Works ── */}
      <section className="py-24 lg:py-32 bg-white dark:bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-3">
                  Featured Works
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400">
                  Selected titles from the M. A. AMIN Books catalog.
                </p>
              </div>
              <Link
                to="/library"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#C5A572] hover:gap-3 transition-all duration-300"
              >
                Full Library
                <ArrowRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((book, i) => (
              <BookCard key={book.id} book={book} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Current Writing Projects ── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A572] mb-4 block">
                The Writing Desk
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-4">
                Current Writing Projects
              </h2>
              <p className="text-zinc-500 dark:text-zinc-400 max-w-xl mx-auto">
                An overview of all active manuscripts and works in development.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {writingProjects.map((project, i) => (
              <ScrollReveal key={project.id} delay={i * 80}>
                <Link
                  to={`/book/${project.id}`}
                  className="group block p-6 rounded-2xl bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 hover:border-[#C5A572]/30 hover:shadow-lg hover:shadow-[#C5A572]/5 transition-all duration-400"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-xs uppercase tracking-[0.12em] font-semibold text-zinc-400 dark:text-zinc-500">
                      {project.genre}
                    </span>
                    {project.status === "complete" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40">
                        <CheckCircle size={9} />
                        Complete
                      </span>
                    ) : project.status === "forthcoming" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-[#C5A572]/10 text-[#C5A572] border border-[#C5A572]/30">
                        <Clock size={9} />
                        Coming Soon
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40">
                        <Clock size={9} />
                        In Progress
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-lg font-bold text-zinc-900 dark:text-white group-hover:text-[#C5A572] transition-colors duration-300 mb-1">
                    {project.title}
                  </h3>
                  {project.series && (
                    <p className="text-xs text-[#C5A572] mb-2">{project.series}</p>
                  )}
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-3 flex items-center gap-1.5">
                    <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform duration-300" />
                    {project.statusLabel}
                  </p>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quote ── */}
      <section className="py-24 lg:py-32 bg-white dark:bg-zinc-900/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="relative">
              <span className="font-serif text-8xl text-[#C5A572]/20 absolute -top-10 left-1/2 -translate-x-1/2">
                &ldquo;
              </span>
              <blockquote className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-zinc-800 dark:text-zinc-200 leading-relaxed italic">
                Every story is an act of faith, a belief that what you have imagined is worth the reader's time, and that the truth inside it will find its way home.
              </blockquote>
              <div className="mt-8 flex items-center justify-center gap-3">
                <div className="w-8 h-px bg-[#C5A572]" />
                <span className="text-sm font-medium text-[#C5A572]">M. A. AMIN</span>
                <div className="w-8 h-px bg-[#C5A572]" />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 lg:py-32 bg-zinc-900 dark:bg-zinc-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Get in Touch
            </h2>
            <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
              Reader inquiries, beta reading opportunities, review requests, collaborations, and publishing discussions are welcome.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#C5A572] text-white rounded-full text-sm font-semibold transition-all duration-300 hover:bg-[#D4BC8E] hover:shadow-xl hover:shadow-[#C5A572]/20"
            >
              Contact M. A. AMIN Books
              <ArrowRight size={14} />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
